import { v4 as uuid } from 'uuid'
import { memo, MouseEventHandler, useCallback, useState } from 'react'
import { IconButton, ListItemText, MenuItem } from '@mui/material'
import FavouriteIcon from '../../assets/icons/favourite'
import Rename from '../../assets/icons/Rename'
import BinIcon from '../../assets/icons/BinIcon'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import ReusableDialog from '../common/reusableDialog'
import { deleteUserDashboard as deleteUserDashboardApi, saveUserDashboard, updateUserDashboardDefault } from '../../services/apis/dashboard'
import { useDispatch, useSelector } from 'react-redux'
import commonActions from '../../actions/common.actions'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import { selectUserDashboards } from '../../selectors/layout'
import { selectActiveLayoutId } from '../../selectors/home'
import { WidgetConfig } from '../widgetDashboard/widgetDashboard.interface'
import { selectUnderlyingOptionsExpiriesAll } from '../../selectors/options'
import { selectSnapshots } from '../../selectors/security'
import updateUtils from '../multiPaneMenu/utils/updateSecurityUtil'
import { updateSavedChartConfigs } from '../../reducer/chart.reducer'
import { updateLayout } from '../../reducer/widgetDashboard.reducer'
import { deleteUserDashboard, setDefaultDashboard, setUserDashboards, updateUserDashboard } from '../../reducer/user.reducer'
import { DashboardShareStatus, Platform, platformFromJSON, UserDashboardProto } from '../../proto/generated/user-preference'
import { selectIsMobile } from '../../selectors/device'
import { normalizePlatform } from '../widgetDashboard/widgetDashboard.helper'
import { getSecurityAsync } from '../../util/security'


export type LayoutMenuItemProps = {
    dashboard: UserDashboardProto,
    active: boolean,
    onRenameClick: (id: string) => void,
    closeMenu: () => void
}

const LayoutMenuItem: React.FC<LayoutMenuItemProps> = ({dashboard, active, onRenameClick, closeMenu}) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const dashboards = useSelector(selectUserDashboards);
    const activeLayoutId = useSelector(selectActiveLayoutId);
    const optionsExpiriesAll = useSelector(selectUnderlyingOptionsExpiriesAll())!!
    const underlyingSnaphots = useSelector(selectSnapshots);
    const isMobile = useSelector(selectIsMobile)
    const platform = isMobile?Platform.MOBILE:Platform.DESKTOP
    const isPlatformDifferent = (isMobile && platformFromJSON(dashboard.platform) !== Platform.MOBILE) || (!isMobile && platformFromJSON(dashboard.platform) === Platform.MOBILE);
    const platformDashboards = dashboards.filter(d => !d.isPredefined && normalizePlatform(d.platform) === platform);

    const onDeleteClick: MouseEventHandler = useCallback((e) => {
        e.stopPropagation()
        setShowDeleteDialog(true);
    }, [])


    const onSetAsDefaultClick: MouseEventHandler = useCallback(async (e) => {
        e.stopPropagation();
        const updatedDashboard = await updateUserDashboardDefault({ dashboard });
        dispatch(setDefaultDashboard({ id: updatedDashboard.id, platform: isMobile ? Platform.MOBILE : Platform.DESKTOP }))
    }, [dashboard, dispatch, isMobile])

    const onDeleteConfirm = useCallback(async () => {
        let newDefaultId = await deleteUserDashboardApi({
            dashboard: {
                id: dashboard.id
            }
        });

        if (!newDefaultId && dashboards.length) {
            const defaultDashboard = dashboards.find(d => d.isDefault && normalizePlatform(d.platform) === platform)
            if (!defaultDashboard) {
                const result = await updateUserDashboardDefault({
                    dashboard: {
                        ...dashboards[0],
                        isDefault: true
                    }
                });
                newDefaultId = result.id;
                dispatch(updateUserDashboard(result));

            } else {
                newDefaultId = defaultDashboard.id;
            }
        }
        commonActions.setApiError(dispatch, {
            error: true,
            message: 'Layout deleted successfully',
            errorType: 'success'
        })
        AnalyticsManager.logEvent('Layout deleted Successfully', {
            source: 'save-menu',
            name: dashboard.name
        });

        if (activeLayoutId === dashboard.id) {
            if (newDefaultId) {
                navigate(generatePath(ROUTES.chart, {layoutId: newDefaultId}), {replace: true});
            } else {
                navigate(ROUTES.base, {replace: true});
            }
            closeMenu();
        }
        dispatch(deleteUserDashboard({ deletedLayoutId: dashboard.id, defaultLayoutId: newDefaultId as string}));

    }, [dashboard, closeMenu, dashboards, activeLayoutId, platform]);

    const onDeleteClose = useCallback(() => {
        setShowDeleteDialog(false);
    }, []);

    const onPredefinedDashboardClicked = useCallback(async () => {
        const { id, layout, name } = dashboard
        const newLayoutId = uuid().replace(/[-]*/g, '').split('').slice(0, 10).join('')
        const updatedLayout = layout.replace(new RegExp(id, 'g'), newLayoutId)
        const updatedParsedLayout = JSON.parse(updatedLayout)
        const charts = {
            ...updatedParsedLayout.charts
        }
        const drawings = {
            ...updatedParsedLayout.drawings
        }
        const layoutToBeSaved = {
            panes: updatedParsedLayout.panes,
            syncSettings: updatedParsedLayout.syncSettings,
            widgets: updatedParsedLayout.widgets,
            drawings: updatedParsedLayout.drawings,
            charts: updatedParsedLayout.charts
        }
        const widgets: Record<string, WidgetConfig<any>> = updatedParsedLayout.widgets
        for (const w_key of Object.keys(widgets)) {
            const type = layoutToBeSaved.widgets[w_key].widgetType
            const key = widgets[w_key as string]?.settings?.key
            const optionExpiriesData = optionsExpiriesAll[key] ? [optionsExpiriesAll[key]] : undefined;
            const underlyingSnaphot = underlyingSnaphots[key]
            const nearAtm = layoutToBeSaved.charts[`${newLayoutId}-${w_key}`]?.multiStrikeChartSettings?.nearAtm
            const security = await getSecurityAsync(key);
            const updates = await updateUtils(
                dispatch,
                newLayoutId,
                layoutToBeSaved,
                optionExpiriesData,
                security,
                key ?? "NSE:NIFTY_50",
                underlyingSnaphot,
                type,
                w_key,
                nearAtm ?? 3,
            );
            // update chart-multiStrikeConfig
            if (
                Object.keys(updates?.securities ?? {}).length > 0 &&
                layoutToBeSaved.charts[`${newLayoutId}-${w_key}`] &&
                layoutToBeSaved.charts[`${newLayoutId}-${w_key}`]?.multiStrikeChartSettings
            ) {
                layoutToBeSaved.charts = {
                    ...layoutToBeSaved.charts,
                    [`${newLayoutId}-${w_key}`]: {
                        ...layoutToBeSaved.charts[`${newLayoutId}-${w_key}`],
                        multiStrikeChartSettings: {
                            ...layoutToBeSaved.charts[`${newLayoutId}-${w_key}`]?.multiStrikeChartSettings,
                            ...(nearAtm ? { nearAtm: nearAtm } : { securities: updates?.securities }),
                            securities: updates?.securities,
                            expiry: updates?.expiry,
                            key: key
                        }
                    }
                };
            }
        }

        dispatch(updateSavedChartConfigs({
            charts,
            drawings
        }))
        

        try {
            const result = await saveUserDashboard({
                dashboard: {
                    id: newLayoutId,
                    layout: JSON.stringify(layoutToBeSaved),
                    name: name,
                    isDefault: dashboard?.isDefault || false,
                    isPredefined: false,
                    shareState: DashboardShareStatus.NOT_SHARED,
                    platform: isMobile ? Platform.MOBILE : Platform.DESKTOP
                }
            });
            if (!dashboards.find((d) => d.id === newLayoutId)?.name) {
                dispatch(setUserDashboards([...dashboards, result]))
            }
            const copiedLayout = {
                name: `${name}`,
                panes: layoutToBeSaved.panes,
                singleMode: layoutToBeSaved.panes.panes.length === 1,
                syncSettings: layoutToBeSaved.syncSettings,
                widgets: layoutToBeSaved.widgets,
                drawings: layoutToBeSaved.drawings,
                charts: layoutToBeSaved.charts
            }
            dispatch(updateLayout({ id: newLayoutId, layout: copiedLayout }))
            navigate(generatePath(ROUTES.chart, { layoutId: newLayoutId }))
        } catch (error) {
            console.error("Unexpected error:", error);
        }

    }, [dashboard, optionsExpiriesAll, underlyingSnaphots, dashboards])

    const onClick: MouseEventHandler = useCallback(async (e) => {
        e.stopPropagation()
        closeMenu();
        if (dashboard.isPredefined) {
            onPredefinedDashboardClicked();
        } else {
            navigate(generatePath(ROUTES.chart, { layoutId: dashboard.id }))
        }
    }, [closeMenu, dashboard.isPredefined, dashboard.id, onPredefinedDashboardClicked, navigate]);

    const onRenameClickHandler = useCallback(() => {
        onRenameClick(dashboard.id)
    }, [dashboard, onRenameClick])

    return <>
            <MenuItem
                key={dashboard.id}
                selected={active}
                onClick={isPlatformDifferent ? undefined : onClick}
                data-event={'Saved Layout Selected'}
                data-event-source={'save-menu'}
                data-event-layout={dashboard.name}
                sx={{
                    '&:hover .MuiIconButton-root': {
                        visibility: 'visible'
                    },
                }}
            >
                <ListItemText primary={dashboard.name} primaryTypographyProps={{ color: isPlatformDifferent ? 'text.secondary' : 'text.primary' }} />
                {!dashboard.isPredefined  ? <>
                    {platformDashboards.length > 1 ? <IconButton
                        size={'small'}
                        sx={{ visibility: 'hidden', height: '24px', width: '24px', p: 0, mr: 0.25}}
                        onClick={onDeleteClick}
                        disabled={isPlatformDifferent}
                    >
                        <BinIcon fontSize="inherit" sx={{height: '22px', width: '22px'}} />
                    </IconButton> : null}
                    <IconButton
                        size={'small'}
                        sx={{ visibility: 'hidden', height: '24px', width: '24px', p: 0, mr: 0.5}}
                        onClick={onRenameClickHandler}
                    >
                        <Rename  />
                    </IconButton>
                    <IconButton
                        size={'small'}
                        sx={{ visibility: dashboard.isDefault ? 'visible' : 'hidden', height: '20px', width: '20px', p: 0}}
                        onClick={onSetAsDefaultClick}
                        disabled={isPlatformDifferent}
                    >
                        <FavouriteIcon filled={dashboard.isDefault} sx={{height: '20px', width: '20px'}} />
                    </IconButton>
                </>: null}
                
            </MenuItem>
            {showDeleteDialog && <ReusableDialog
                open
                onClose={onDeleteClose}
                submitText={'Delete'}
                onSubmit={onDeleteConfirm}
                content={`Do you want to delete layout '${dashboard.name ?? ''}'?`}
            />}
        </>
};

export default memo(LayoutMenuItem);